import { Labels, useDescribeHooks, describeHelpers, useDataTypesHooks } from "../../../../../features/data-types"
import { RoutePath } from "../../../../../features/routes"
import { Action, Async, DetailFieldProps, DetailPanel, FieldType, FieldValue, ModalId, Tab, UIPermission, useGetCrumbs, useUIActions } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { BranchBadge } from "../../../../manager"
import { Operation, OperationLink } from "../../../../operations"
import { useMemo, useState } from "react"
import { Delivery, DeliveryMethod, DeliveryOrder, DeliveryStatus } from "../../../state/types"
import { AnnulDeliveryOrderModal } from "../annul-delivery-order-modal/annulDeliveryOrderModal"
import { DeliveryOrderItemsStatsTable } from "../delivery-order-items-stats-table/deliveryOrderItemsStatsTable"
import { DeliveryDataList } from "../../delivery/delivery-data-list/deliveryDataList"
import { addressHelpers } from "../../../../../features/address"
import { deliveryOrderHelpers, useDeliveryOrderHooks } from "../../../hooks/deliveryOrderHooks"
import { Customer, CustomerLink } from "../../../../sales"
import { CompanyUserName, useSystemStateHooks } from "../../../../system"
import { useNavigate } from "react-router-dom"
import { deliveryHelpers, useDeliveryHooks } from "../../../hooks/deliveryHooks"
import { DownloadDeliveryOrderNotesModal } from "../download-delivery-order-notes-modal/downloadDeliveryOrderNotesModal"
import { toList } from "../../../../../utils/listUtils"
import { DownloadDeliveryNoteModal } from "../../delivery/download-delivery-note-modal/downloadDeliveryNoteModal"
import { UploadDeliveryNoteModal } from "../../delivery/upload-delivery-note-modal/uploadDeliveryNoteModal"

type Props = {
    deliveryOrder: DeliveryOrder
    operation: Operation
    deliveries: Delivery[]
    customer?: Customer
}

export const DeliveryOrderDetail = (props: Props) => {
    const { deliveryOrder, operation, customer } = props
    const { items, notDeliveredCount, ...data } = deliveryOrder

    const [deliveries, setDeliveries] = useState<Delivery[]>(props.deliveries)
    const [currentDelivery, setCurrentDelivery] = useState<Delivery>()

    const { userHasPermission } = useSystemStateHooks()
    const { formatDateTime } = useDataTypesHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_SALE_DELIVERIES)
    const { isDeliverable, isDeliveryCompletable, isAnnulable, getDeliveryMethodRoutPath, getDeliveryMethodIcon } = deliveryOrderHelpers
    const { getCreateDeliveryLabel, getCreateDeliveryIcon } = deliveryHelpers
    const { describeCode } = describeHelpers
    const { describeAddress } = addressHelpers
    const { describe } = useDescribeHooks()
    const { refreshDeliveries } = useDeliveryHooks()
    const { getDeliveryAddress } = useDeliveryOrderHooks()
    const navigate = useNavigate()
    
    const { openModal } = useUIActions()

    const title = useMemo(() => describe(TableName.DELIVERY_ORDERS, deliveryOrder), [deliveryOrder])
    const crumbs = useGetCrumbs(
        `Órdenes de ${Labels.deliveryMethodLabel(deliveryOrder.method)}`,
        getDeliveryMethodRoutPath(deliveryOrder.method),
        describeCode(deliveryOrder)
    )

    const fields: DetailFieldProps[] = [
        {
            name: 'method',
            type: FieldType.TEXT,
            label: 'Método de Entrega',
            render: (value?: FieldValue) => <>{Labels.deliveryMethodLabel(value! as DeliveryMethod)}</>
        },
        {
            name: 'operationId',
            type: FieldType.TABLE,
            table: deliveryOrder.table,
            label: 'Operación',
            loading: !operation,
            render: () => (
                <OperationLink
                    table={deliveryOrder.table}
                    operation={operation}
                />
            )
        },
        {
            name: 'branch',
            type: FieldType.TEXT,
            label: `Sucursal${deliveryOrder.method === DeliveryMethod.PICKUP ? ' de Entrega': ''}`,
            render: () => <BranchBadge branchId={deliveryOrder.branchId} />
        },
        {
            name: 'deliveryAddress',
            type: FieldType.TEXT,
            label: 'Dirección de Entrega',
            render: () => <Async
                loadFn={async () => {
                    const address = await getDeliveryAddress(deliveryOrder)!
                    return <>{describeAddress(address)}</>
                }}
            />
        },
        {
            name: 'deliveryDate',
            type: FieldType.DATE,
            label: 'Fecha de Entrega'
        },
        {
            name: 'items',
            type: FieldType.TABLE,
            render: () => <DeliveryOrderItemsStatsTable
                deliveryOrder={deliveryOrder}
                operation={operation}
            />
        },
        {
            name: 'deliveryStatus',
            type: FieldType.TEXT,
            label: 'Estado de Entrega',
            render: (value?: FieldValue) => <>{Labels.deliveryStatusLabel(value! as DeliveryStatus)}</>
        }
    ]

    if (customer) {
        const customerField: DetailFieldProps = {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente',
            render: () => (
                <CustomerLink customer={customer} />
            )
        }
        fields.splice(2, 0, customerField)
    }

    if (deliveryOrder.negativeStateUserId) {
        fields.push({
            name: 'negativeStateUserId',
            type: FieldType.TEXT,
            label: 'Anulada por',
            render: () => {
                const dateStr = deliveryOrder.negativeStateAt ? ` - ${formatDateTime(new Date(deliveryOrder.negativeStateAt))}` : ''
                return <><CompanyUserName userId={deliveryOrder.negativeStateUserId!} />{dateStr}</>
            }
        })
    }

    const tabs: Tab[] = useMemo(() => {
        const tabList: Tab[] = []
        if (deliveries.length > 0) {
            tabList.push({
                id: 'deliveries',
                title: 'Entregas',
                content: <DeliveryDataList
                    deliveries={deliveries}
                    deliveryOrders={toList(deliveryOrder)}
                    operations={toList(operation)}
                    onChangeCurrentDelivery={setCurrentDelivery}
                />
            })
        }
        return tabList
    }, [deliveries, deliveryOrder, operation])

    const onCreateDelivery = () => navigate(
        deliveryOrder.method === DeliveryMethod.SHIPMENT ?
            RoutePath.GENERATE_DELIVERY_NOTE :
            RoutePath.COMPLETE_DELIVERY
    )

    const onCompleteDelivery = () => navigate(RoutePath.COMPLETE_DELIVERY)

    const onAnnulDeliveryOrder = () => openModal(ModalId.ANNUL_DELIVERY_ORDER)

    const onDownloadDeliveryNotes = () => openModal(ModalId.DOWNLOAD_FILES)

    const actions: Action[] = useMemo(() => {
        const actionList: Action[] = []

        const isReception = deliveryOrder.method === DeliveryMethod.RECEPTION
        const isShipment = deliveryOrder.method === DeliveryMethod.SHIPMENT
        const hasDeliveries = deliveries.length > 0
        const hasSignedDeliveryNotes = deliveries.some(delivery => delivery.deliveryNoteUrl)

        if (isReception ? hasSignedDeliveryNotes : hasDeliveries) {
            actionList.push({ label: 'Remitos', icon: 'download', kind: 'tertiary', onClick: onDownloadDeliveryNotes })
        }
        if (!readOnly) {
            if (isDeliverable(deliveryOrder)) {
                actionList.push({
                    label: getCreateDeliveryLabel(deliveryOrder.method),
                    icon: getCreateDeliveryIcon(deliveryOrder.method),
                    onClick: onCreateDelivery
                })
            }
            if (isShipment && isDeliveryCompletable(deliveryOrder)) {
                actionList.push({ label: 'Completar Envío', icon: 'bag-check-fill', onClick: onCompleteDelivery })
            }
            if (isAnnulable(deliveryOrder)) {
                const annulLabel = `Anular ${hasDeliveries ? 'Pendientes' : 'Orden'}`
                actionList.push({ label: annulLabel, icon: 'slash-circle', kind: 'secondary', onClick: onAnnulDeliveryOrder })
            }
        }

        return actionList
    }, [deliveryOrder, deliveries, isDeliverable, isAnnulable, readOnly])

    const onUploadDeliveryNote = async () => {
        const refreshedDeliveries = await refreshDeliveries(deliveries)
        setDeliveries(refreshedDeliveries)
    }

    return (
        <>
            <DetailPanel
                tableName={TableName.DELIVERY_ORDERS}
                title={title}
                crumbs={crumbs}
                fields={fields}
                data={data}
                icon={getDeliveryMethodIcon(deliveryOrder.method)}
                imgSpace={3}
                readOnly
                actions={actions}
                tabs={tabs}
            />
            <DownloadDeliveryNoteModal
                delivery={currentDelivery}
                deliveryOrder={deliveryOrder}
            />
            <DownloadDeliveryOrderNotesModal deliveryOrder={deliveryOrder} />
            <UploadDeliveryNoteModal
                delivery={currentDelivery}
                deliveryOrder={deliveryOrder}
                onUpload={onUploadDeliveryNote}
            />
            <AnnulDeliveryOrderModal
                deliveryOrder={deliveryOrder}
                operation={operation}
            />
        </>
    )
}
